import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from "react-helmet-async";

import SVGSeparator from '../services/SVGSeparator';
import products_fr from '../data/products_fr';
import products_en from '../data/products_en';

const quinceImg = '/assets/img/quince.png';

/* Apparition au scroll (meme hook que le reste du site). */
const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
};

const ProductCard = ({ product, index, onDiscover, discoverLabel }) => {
  const [ref, visible] = useReveal();
  const teaser = Array.isArray(product.description) ? product.description[0] : product.description;

  return (
    <div
      ref={ref}
      className={`group flex flex-col bg-white rounded-2xl border overflow-hidden shadow-md transition-all duration-700 ease-out hover:shadow-2xl hover:-translate-y-1 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ borderColor: 'rgba(120,90,40,0.2)', transitionDelay: `${(index % 3) * 80}ms` }}
    >
      {/* Image (fond plus marque + filet de separation pour le contraste) */}
      <button
        onClick={onDiscover}
        className="relative flex items-center justify-center h-72 overflow-hidden border-b"
        style={{
          background: 'linear-gradient(160deg, #EFE2CB 0%, #E4D2B2 100%)',
          borderColor: 'rgba(120,90,40,0.18)',
        }}
        aria-label={product.title}
      >
        <img
          src={product.imageSm}
          alt={product.title}
          loading="lazy"
          decoding="async"
          className="h-full w-auto object-contain p-6 drop-shadow-md transition-transform duration-500 group-hover:scale-105"
        />
      </button>

      {/* Details */}
      <div className="flex flex-col flex-1 p-6">
        <h2 className="font-sans text-lg md:text-xl text-stone-900">{product.title}</h2>
        {teaser && (
          <p className="font-nunito text-sm text-stone-500 mt-2 leading-relaxed line-clamp-2">
            {teaser}
          </p>
        )}

        {/* Prix sur sa propre ligne (ne se coupe plus) */}
        <div className="mt-auto pt-6">
          <div className="font-sans text-3xl whitespace-nowrap mb-4" style={{ color: '#A9760F' }}>
            {product.price.toFixed(2)}&nbsp;€
          </div>
          {/* Bouton dedie pleine largeur (contraste fort) */}
          <button
            onClick={onDiscover}
            className="w-full font-sans tracking-wide rounded-xl py-3.5 text-white transition-all duration-300 hover:opacity-90 hover:scale-[1.02]"
            style={{ backgroundColor: '#2E2318' }}
          >
            {discoverLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const Boutique = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const products = i18n.language === 'fr' ? products_fr : products_en;

  const [headRef, headVisible] = useReveal();

  return (
    <div style={{ backgroundColor: '#FBF7EF' }}>
      <Helmet>
        <title>Boutique | Au Coing du Ventoux</title>
        <meta name="description" content="Commandez notre liqueur et eau-de-vie de coing artisanale. Vente en ligne avec expedition rapide." />
        <meta name="keywords" content="acheter alcool de coing, boutique artisanal, liqueur de coing en ligne" />
        <meta property="og:title" content="Boutique | Au Coing du Ventoux" />
        <meta property="og:description" content="Commandez notre liqueur et eau-de-vie de coing artisanale. Vente en ligne avec expedition rapide." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aucoingduventoux.com/boutique" />
        <meta property="og:image" content="https://aucoingduventoux.com/images/boutique.jpg" />
        <link rel="canonical" href="https://aucoingduventoux.com/boutique" />
      </Helmet>

      <div className="relative overflow-hidden pt-40 md:pt-52 pb-24">
        {/* Coings decoratifs sur les cotes */}
        <img src={quinceImg} alt="" aria-hidden="true" loading="lazy"
          className="hidden md:block absolute top-44 -left-10 w-24 opacity-[0.07] pointer-events-none" />
        <img src={quinceImg} alt="" aria-hidden="true" loading="lazy"
          className="hidden md:block absolute top-[40%] -right-10 w-32 opacity-[0.07] pointer-events-none" />
        <img src={quinceImg} alt="" aria-hidden="true" loading="lazy"
          className="hidden md:block absolute bottom-40 -left-8 w-28 opacity-[0.07] pointer-events-none" />

        {/* En-tete */}
        <div
          ref={headRef}
          className={`text-center px-6 mb-16 transition-all duration-1000 ease-out ${
            headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-800">
            Boutique
          </span>
          <h1 className="font-sans mt-5 text-4xl md:text-5xl lg:text-6xl text-stone-900">
            {t('boutique.title')}
          </h1>
          <SVGSeparator width={120} height={50} className="mx-auto" />
          <p className="mt-4 mx-auto max-w-2xl font-nunito text-lg text-stone-600">
            {t('home.description')}
          </p>
        </div>

        {/* Grille produits */}
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 px-6 md:px-10">
          {products.map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              index={index}
              discoverLabel={t('boutique.product.discover')}
              onDiscover={() => navigate(`/product/${product.id}`)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};